import { float, smoothstep } from 'three/tsl';
import type { TerrainProfile } from '../terrain/TerrainProfile';
import { AUTUMN, verdantAutumn } from '../terrain/TerrainStyle';

type N = any;
type RGB = [number, number, number];

/** Colours of a hollow's grass. */
export interface GrassStyle {
  lush: RGB;
  meadow: RGB;
  tip: RGB;
  /**
   * The share of blades whose upper half turns the `tip` colour (dry grass); the rest are
   * fresh, lighter at the tip. Left out, every blade leans toward `tip` by a random amount
   * (frosted or tinted tips all over).
   */
  dryTips?: number;
  roughness: number;
  /** Grass starts growing this high above the water line (m). */
  minHeight?: number;
  /** Blade height multiplier. */
  height?: number;
  /**
   * Optional glow at the blade tips: builds a radiance node, evaluated per fragment
   * (it may use `positionWorld`, e.g. to follow a light rig's glow phase). In a world of
   * several biomes it gets the biome weights under the blade (vec4; see BiomeField).
   */
  tipGlow?: (biome?: N) => N;
  /** Optional glow of the whole blade (e.g. to match glowing ground beneath it). */
  glow?: (biome?: N) => N;
  /**
   * Colours and growth that change from place to place (a world of several biomes), at a
   * blade's root: `p` hollow-local, `h` as the palette sees heights, `biome` the biome
   * weights there (see BiomeField). Replaces lush, meadow and tip; `grow` and `height`
   * multiply the blades' own.
   */
  local?: (ctx: { p: N; h: N; slope: N; biome?: N }) => { lush: N; meadow: N; tip: N; grow: N; height: N };
}

/**
 * Tree shapes. Each has parts: 0 = trunk/stalk, 1 = crown/cap, 2 = glow organ
 * (a mushroom's gills, a lantern bulb).
 */
export type TreeShape =
  | 'conifer'
  | 'broadleaf'
  | 'mushroom'
  | 'lantern'
  | 'crystal'
  | 'boulder'
  | 'palm'
  | 'cactus'
  | 'acacia'
  | 'dead'
  | 'shrub';

export interface SpeciesStyle {
  shape: TreeShape;
  bark: RGB;
  /** Crown colour range (picked per tree). */
  crown: [RGB, RGB];
  /** How far crown normals bend toward "up" (a cheap stand-in for light through leaves). */
  bendUp: number;
  scale?: number;
  /** Radiance of the glow organ (part 2); the crown gets `crownGlow` of it. */
  glow?: () => N;
  crownGlow?: number;
  /** Wind sway multiplier (0 for rocks). */
  sway?: number;
  /**
   * A soft point light at the glow organ, drawn as a deferred light volume. `height` is
   * the organ's height above the ground and `radius` the light's reach (both scale with
   * the plant, m); `gain` turns the organ's glow radiance into light. A `hooded` light only
   * shines downward (nothing above the organ is lit).
   */
  light?: { height: number; radius: number; gain: number; hooded?: boolean };
  /** Seasonal crown colour: where `at(p)` (hollow-local ground position) is 1, crowns take `colors`. */
  seasonal?: { at: (p: N) => N; colors: [RGB, RGB] };
}

/** What a tree layer knows about a spot: hollow-local ground point, palette height, slope, biome weights. */
export interface TreeContext {
  p: N;
  h: N;
  slope: N;
  profile: TerrainProfile;
  /** The biome weights there (vec4; see BiomeField), in a world of several biomes. */
  biome?: N;
  /** Its gullies and ridges (vec2; see SurfaceContext.ridge), with erosion. */
  ridge?: N;
}

export interface TreeStyle {
  a: SpeciesStyle;
  b: SpeciesStyle;
  /** Share of species A on low ground (h < 150 m) and high ground (h > 500 m). */
  aShare: [number, number];
  /** Where this layer's forests grow (0..1); defaults to the terrain style's forest mask. */
  mask?: (ctx: TreeContext) => N;
  /**
   * Where this layer may grow at all (0..1), forests and lone trees alike: e.g. the share of
   * the ground its biome has (default 1).
   */
  where?: (ctx: TreeContext) => N;
  /** Draw distance (m); default 520. */
  range?: number;
  /** Trees start growing this high above the water line (m). */
  minHeight?: number;
  /** Density of lone trees outside forests (0..~0.03). */
  loneTrees: number;
}

export const VERDANT_GRASS: GrassStyle = {
  lush: [0.08, 0.14, 0.03],
  meadow: [0.19, 0.23, 0.065],
  tip: [0.34, 0.34, 0.14],
  dryTips: 0.18,
  roughness: 0.62,
};

/**
 * Below the tree line (0..1): trees thin out from 55% of the snow line and are gone by 80%, as
 * the forests do (verdantForest). Lone trees heed it too: without it they stood on the snowfields.
 */
export const belowTreeLine = (ctx: TreeContext): N =>
  float(1).sub(smoothstep(ctx.profile.snowLine * 0.55, ctx.profile.snowLine * 0.8, ctx.h));

export const VERDANT_TREES: TreeStyle = {
  where: belowTreeLine,
  a: {
    shape: 'conifer',
    bark: [0.12, 0.09, 0.06],
    crown: [
      [0.028, 0.068, 0.032],
      [0.048, 0.1, 0.044],
    ],
    bendUp: 0.55,
  },
  b: {
    shape: 'broadleaf',
    bark: [0.12, 0.09, 0.06],
    crown: [
      [0.045, 0.09, 0.022],
      [0.095, 0.135, 0.036],
    ],
    bendUp: 0.55,
    seasonal: { at: verdantAutumn, colors: AUTUMN },
  },
  aShare: [0.25, 0.85],
  loneTrees: 0.01,
};
